import React, { useCallback, useMemo, useState } from 'react';
import { AgGridTable, Badge, confirmAlert, TwIconButton } from 'components/common';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import { deleteContactCustomer, getCustomer } from 'redux/crm/customerSlice';
import EditContactDrawer from 'components/app/crm/contact/EditContactDrawer';
import { formatPhoneNumberInternational, formatPhoneNumberUrl } from 'helpers/phoneNumber';

const CONTACT_URL = '/crm/contacts';

const ContactTable = ({ customer, contacts }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customerId = customer?.id ?? null;

  const [editContact, setEditContact] = useState(null);

  const handleView   = useCallback((c) => navigate(`${CONTACT_URL}/${c.id}`), [navigate]);
  const handleEdit   = (c) => setEditContact(c);
  const handleDelete = useCallback((c) => {
    confirmAlert({
      title: 'Delete contact',
      message: `Are you sure you want to delete contact "${c.name}"?`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        { label: 'Yes', variant: 'danger', onClick: () => dispatch(deleteContactCustomer({ customerId, contactId: c.id })) },
        { label: 'No',  onClick: () => {} },
      ],
    });
  }, [dispatch, customerId]);

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'ID#',
      minWidth: 70,
      maxWidth: 80,
      cellRenderer: (p) => (
        <a href={`${CONTACT_URL}/${p.data.id}`} target="_blank" rel="noreferrer" className="tw-text-blue-600 hover:tw-underline">
          {p.data.id}
        </a>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 150,
      cellRenderer: (p) => (
        <a href={`${CONTACT_URL}/${p.data.id}`} target="_blank" rel="noreferrer" className="tw-font-medium tw-text-blue-600 hover:tw-underline">
          {p.data.name}
        </a>
      ),
    },
    { field: 'position',   headerName: 'Title',      minWidth: 120 },
    { field: 'department', headerName: 'Department',  minWidth: 120 },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 180,
      cellRenderer: (p) => p.data.email ? (
        <a href={`mailto:${p.data.email}`} className="tw-text-blue-600 hover:tw-underline">{p.data.email}</a>
      ) : null,
    },
    {
      field: 'mobile',
      headerName: 'Phone',
      minWidth: 140,
      cellRenderer: (p) => p.data.mobile ? (
        <a href={formatPhoneNumberUrl(p.data.mobile)} className="tw-text-blue-600 hover:tw-underline">
          {formatPhoneNumberInternational(p.data.mobile)}
        </a>
      ) : null,
    },
    { field: 'main_contact', headerName: 'Main contact', minWidth: 110 },
    {
      field: 'secondary_contact',
      headerName: 'Secondary',
      minWidth: 140,
      cellRenderer: (p) => (
        <div className="tw-flex tw-flex-wrap tw-gap-1 tw-items-center tw-h-full">
          {(p.data.secondary_contact || []).map((name, i) => (
            <Badge key={i} variant="info" size="sm">{name}</Badge>
          ))}
        </div>
      ),
    },
    { field: 'created_by', headerName: 'Created by',   minWidth: 110 },
    { field: 'created_at', headerName: 'Created date', minWidth: 150 },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: 90,
      maxWidth: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p) => {
        const { allow_view, allow_edit, allow_delete } = p.data?.permissions || {};
        if (!allow_view && !allow_edit && !allow_delete) return <span className="tw-text-slate-400 tw-text-xs">None</span>;
        return (
          <div className="tw-flex tw-items-center tw-gap-2 tw-h-full">
            {allow_view && (
              <TwIconButton
                icon={FaEye}
                iconSize={13}
                variant="ghost"
                size="sm"
                onClick={() => handleView(p.data)}
                title="View"
              />
            )}
            {allow_edit && (
              <TwIconButton
                icon={FaEdit}
                iconSize={13}
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(p.data)}
                title="Edit"
              />
            )}
            {allow_delete && (
              <TwIconButton
                icon={FaTrash}
                iconSize={13}
                variant="danger"
                size="sm"
                onClick={() => handleDelete(p.data)}
                title="Delete"
              />
            )}
          </div>
        );
      },
    },
  ], [handleView, handleDelete]);


  if (!contacts) return null;

  return (
    <>
      <AgGridTable
        rowData={contacts}
        columnDefs={columns}
        height="380px"
        rowHeight={36}
        headerHeight={40}
        fontSize="12px"
        filter={false}
        sort={false}
        pagination={false}
      />

      <EditContactDrawer
        show={Boolean(editContact)}
        onHide={() => setEditContact(null)}
        customer={customer}
        contact={editContact}
        onSaved={() => { if (customerId) dispatch(getCustomer(customerId)); }}
      />
    </>
  );
};

ContactTable.propTypes = {
  customer: PropTypes.object.isRequired,
  contacts: PropTypes.array.isRequired,
};

export default ContactTable;
